import React, { useEffect, useState, useCallback } from 'react';
import API from '../services/api';

// Cards on File panel for the admin client profile page: view, edit, add,
// label, and hide/unhide a client's stored cards (Authorize.Net). Card data is
// sent straight to Authorize.Net — nothing card-related is stored locally.

function currentYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function isExpired(exp) {
  // exp is 'YYYY-MM' when unmasked; anything unparseable is treated as unknown
  return /^\d{4}-\d{2}$/.test(exp || '') && exp < currentYm();
}
function fmtAdded(d) {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: 14,
  border: '1px solid var(--border-color)', borderRadius: 6,
};
const fieldLabelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-strong)', margin: '8px 0 3px' };
const smallBtn = {
  background: 'var(--surface-muted)', color: 'var(--text-color)',
  border: '1px solid var(--border-color)', borderRadius: 6,
  padding: '3px 9px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const primaryBtn = {
  background: 'var(--primary-color)', color: '#fff', border: 'none',
  borderRadius: 6, padding: '8px 14px', fontWeight: 700, cursor: 'pointer',
};
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16,
};
const boxStyle = {
  background: 'var(--white)', borderRadius: 14, padding: '18px 20px', width: '100%',
  maxWidth: 420, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
};

// Shared form fields for edit/add card modals.
function CardFormFields({ form, setForm, numberOptional }) {
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={fieldLabelStyle}>First Name</label>
          <input style={inputStyle} value={form.firstName} onChange={set('firstName')} />
        </div>
        <div>
          <label style={fieldLabelStyle}>Last Name</label>
          <input style={inputStyle} value={form.lastName} onChange={set('lastName')} />
        </div>
      </div>
      <label style={fieldLabelStyle}>Company / Note (e.g. "HSA", "USE THIS", "Mom's card")</label>
      <input style={inputStyle} value={form.companyName} onChange={set('companyName')} />
      <label style={fieldLabelStyle}>
        Card Number{numberOptional ? ' — leave blank to keep the current card' : ''}
      </label>
      <input
        style={inputStyle} value={form.cardNumber} onChange={set('cardNumber')}
        placeholder={numberOptional ? '•••• keep existing number' : ''}
        inputMode="numeric" autoComplete="off"
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={fieldLabelStyle}>Expiration (YYYY-MM)</label>
          <input style={inputStyle} value={form.expirationDate} onChange={set('expirationDate')} placeholder="2028-04" />
        </div>
        <div>
          <label style={fieldLabelStyle}>CVV{numberOptional ? ' (only with new number)' : ''}</label>
          <input style={inputStyle} value={form.cardCode} onChange={set('cardCode')} inputMode="numeric" autoComplete="off" />
        </div>
      </div>
    </>
  );
}

export default function CardsOnFilePanel({ client }) {
  const [cards, setCards] = useState([]);
  const [hiddenSet, setHiddenSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHidden, setShowHidden] = useState(false);

  const [editCard, setEditCard] = useState(null);   // card being edited
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  const [labelFor, setLabelFor] = useState(null);   // paymentProfileId being label-edited
  const [labelText, setLabelText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [prRes, hidRes] = await Promise.all([
        API.get(`/admin/clients/${client.id}/payment-profiles`),
        API.get('/admin/hidden-cards'),
      ]);
      setCards(prRes.data?.profiles || []);
      const hid = new Set(
        (hidRes.data?.hidden || hidRes.data || [])
          .filter((h) => String(h.client_id) === String(client.id))
          .map((h) => String(h.payment_profile_id))
      );
      setHiddenSet(hid);
    } catch (err) {
      console.error('Error loading cards on file:', err);
      setError('Error loading cards from Authorize.Net.');
    } finally {
      setLoading(false);
    }
  }, [client.id]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (card) => {
    setForm({
      firstName: card.firstName || '',
      lastName: card.lastName || '',
      companyName: card.companyName || '',
      cardNumber: '',
      expirationDate: /^\d{4}-\d{2}$/.test(card.expiration || '') ? card.expiration : '',
      cardCode: '',
    });
    setModalError('');
    setEditCard(card);
  };

  const openAdd = () => {
    setForm({ firstName: '', lastName: '', companyName: '', cardNumber: '', expirationDate: '', cardCode: '' });
    setModalError('');
    setAddOpen(true);
  };

  const saveEdit = async () => {
    if (!/^\d{4}-\d{2}$/.test(form.expirationDate)) {
      setModalError('Expiration must be YYYY-MM (e.g. 2028-04).');
      return;
    }
    setSaving(true);
    setModalError('');
    try {
      await API.put(`/admin/clients/${client.id}/payment-profile/${editCard.paymentProfileId}`, {
        customerProfileId: editCard.customerProfileId,
        maskedCard: editCard.maskedCard,
        cardNumber: form.cardNumber.trim() || undefined,
        cardCode: form.cardCode.trim() || undefined,
        expirationDate: form.expirationDate,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        companyName: form.companyName.trim(),
      });
      setEditCard(null);
      load();
    } catch (err) {
      console.error('Error updating card:', err);
      setModalError(err.response?.data?.message || 'Error updating card.');
    } finally {
      setSaving(false);
    }
  };

  const saveAdd = async () => {
    if (!form.cardNumber.trim() || !/^\d{4}-\d{2}$/.test(form.expirationDate)) {
      setModalError('Card number and a YYYY-MM expiration are required.');
      return;
    }
    setSaving(true);
    setModalError('');
    const payload = {
      cardNumber: form.cardNumber.trim(),
      expirationDate: form.expirationDate,
      cardCode: form.cardCode.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      companyName: form.companyName.trim(),
    };
    try {
      // Add to the client's existing Authorize.Net profile; if they don't have
      // one yet, fall back to creating a new customer profile.
      try {
        await API.post(`/admin/clients/${client.id}/payment-profile/add-card`, payload);
      } catch (err) {
        if (err.response?.status === 400 && /no existing/i.test(err.response?.data?.message || '')) {
          await API.post(`/admin/clients/${client.id}/payment-profile`, payload);
        } else {
          throw err;
        }
      }
      setAddOpen(false);
      load();
    } catch (err) {
      console.error('Error adding card:', err);
      setModalError(err.response?.data?.message || 'Error adding card.');
    } finally {
      setSaving(false);
    }
  };

  const toggleHidden = async (card, hidden) => {
    try {
      await API.post(`/admin/clients/${client.id}/payment-profile/hide`, {
        paymentProfileId: card.paymentProfileId, hidden,
      });
      setHiddenSet((prev) => {
        const next = new Set(prev);
        if (hidden) next.add(String(card.paymentProfileId));
        else next.delete(String(card.paymentProfileId));
        return next;
      });
    } catch (err) {
      console.error('Error updating hidden card:', err);
      alert('Error updating hidden state');
    }
  };

  const saveLabel = async (card) => {
    try {
      await API.put(`/admin/clients/${client.id}/payment-profile/${card.paymentProfileId}/label`, {
        label: labelText.trim(),
      });
      setLabelFor(null);
      load();
    } catch (err) {
      console.error('Error saving label:', err);
      alert('Error saving label');
    }
  };

  const visibleCards = cards.filter((c) => showHidden || !hiddenSet.has(String(c.paymentProfileId)));
  const hiddenCount = cards.filter((c) => hiddenSet.has(String(c.paymentProfileId))).length;

  return (
    <div style={{ background: 'var(--white)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}>Cards on File</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {hiddenCount > 0 && (
            <button style={smallBtn} onClick={() => setShowHidden((s) => !s)}>
              {showHidden ? 'Conceal hidden' : `Show hidden (${hiddenCount})`}
            </button>
          )}
          <button style={{ ...primaryBtn, padding: '6px 12px', fontSize: 13.5 }} onClick={openAdd}>
            + Add Card
          </button>
        </div>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '4px 0 14px' }}>
        Stored securely with Authorize.Net. Editing sends changes straight to
        Authorize.Net — the portal never keeps card numbers.
      </p>

      {loading ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading cards…</div>
      ) : error ? (
        <div style={{ color: 'var(--error-color)' }}>{error}</div>
      ) : visibleCards.length === 0 ? (
        <div style={{ padding: '1rem 0', color: 'var(--text-muted)' }}>
          No cards on file{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}.
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {visibleCards.map((card) => {
            const hidden = hiddenSet.has(String(card.paymentProfileId));
            const expired = isExpired(card.expiration);
            return (
              <div
                key={card.paymentProfileId}
                style={{
                  border: `1px solid ${expired ? 'var(--error-color)' : 'var(--border-color)'}`,
                  borderRadius: 10, padding: '10px 12px', minWidth: 210,
                  background: hidden ? 'var(--surface-muted)' : '#fff',
                  opacity: hidden ? 0.65 : 1,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {card.firstName} {card.lastName}
                  {hidden && <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}> (hidden)</span>}
                </div>
                <div style={{ fontSize: 13, color: '#4b5563', margin: '2px 0' }}>💳 {card.maskedCard}</div>
                <div style={{ fontSize: 12.5, color: expired ? 'var(--error-color)' : 'var(--text-muted)', fontWeight: expired ? 700 : 400 }}>
                  Exp {card.expiration || '—'}{expired ? ' — EXPIRED' : ''}
                </div>
                {card.companyName && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>{card.companyName}</div>
                )}
                {card.label && card.label !== card.companyName && (
                  <div style={{ fontSize: 11, color: '#3730a3', fontWeight: 600 }}>{card.label}</div>
                )}
                {fmtAdded(card.addedAt) && (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Added {fmtAdded(card.addedAt)}</div>
                )}

                {labelFor === card.paymentProfileId ? (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <input
                      style={{ ...inputStyle, padding: '4px 7px', fontSize: 12.5 }}
                      value={labelText} onChange={(e) => setLabelText(e.target.value)}
                      placeholder="Custom label…" autoFocus
                    />
                    <button style={smallBtn} onClick={() => saveLabel(card)}>Save</button>
                    <button style={smallBtn} onClick={() => setLabelFor(null)}>✕</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    <button style={smallBtn} onClick={() => openEdit(card)}>Edit</button>
                    <button
                      style={smallBtn}
                      onClick={() => { setLabelFor(card.paymentProfileId); setLabelText(card.label || ''); }}
                    >
                      Label
                    </button>
                    <button style={smallBtn} onClick={() => toggleHidden(card, !hidden)}>
                      {hidden ? 'Unhide' : 'Hide'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(editCard || addOpen) && form && (
        <div style={overlayStyle} onClick={() => { setEditCard(null); setAddOpen(false); }}>
          <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 2px' }}>{editCard ? 'Edit Card' : 'Add Card'}</h3>
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {editCard
                ? <>Editing 💳 {editCard.maskedCard} for {client.client_name}</>
                : <>New card for {client.client_name}</>}
            </div>

            <CardFormFields form={form} setForm={setForm} numberOptional={!!editCard} />

            {modalError && (
              <div style={{ color: 'var(--error-color)', fontSize: 13.5, marginTop: 8 }}>{modalError}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={{ ...smallBtn, padding: '8px 14px' }} onClick={() => { setEditCard(null); setAddOpen(false); }}>
                Cancel
              </button>
              <button
                style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}
                disabled={saving}
                onClick={editCard ? saveEdit : saveAdd}
              >
                {saving ? 'Saving…' : editCard ? 'Save Changes' : 'Add Card'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
